import * as React from "react";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { MetricGrid } from "@/components/dashboard/MetricGrid";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { EmptyState } from "@/components/dashboard/EmptyState";
import { useQuote } from "@/hooks/queries/useQuote";
import { useInstrumentMetadata } from "@/hooks/queries/useInstrumentSearch";
import type { InstrumentSummary } from "./InstrumentSearch";

interface QuotePayload {
  last_traded_price: number;
  bid_price: number;
  ask_price: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  change: number | null;
  change_percent: number | null;
  timestamp: string;
  provider: string;
}

interface InstrumentMetadataPayload {
  instrument_type: string;
  tick_size: number;
  lot_size: number;
  provider: string | null;
  currency: string | null;
}

interface QuoteCardProps {
  instrument: InstrumentSummary | null;
}

/**
 * Backend v2.1 (Sprint 4.4) enhanced `QuoteDto` - Open/High/Low/Close/
 * Volume/Timestamp/Provider are now genuine quote-response fields (no
 * longer sourced from the separate historical-candle endpoint, which
 * this component previously joined against for OHLCV only).
 * `change`/`change_percent` remain honestly "Not reported by backend"
 * (the backend itself always returns `null` for both - TD-100, no
 * canonical field exists yet). Instrument Type/Tick Size/Lot Size come
 * from the new `GET /market-data/instrument` endpoint
 * (`InstrumentMetadataDto`); Provider/Currency there are `null` when the
 * backend has no such field (TD-101).
 */
export function QuoteCard({ instrument }: QuoteCardProps): React.JSX.Element {
  const quoteQuery = useQuote(instrument?.instrument_token ?? null);
  const metadataQuery = useInstrumentMetadata(instrument?.instrument_token ?? null);

  if (!instrument) {
    return (
      <StatusCard title="Quote Panel">
        <EmptyState message="Select an instrument to view its quote." />
      </StatusCard>
    );
  }

  return (
    <StatusCard title="Quote Panel">
      <QueryStateBoundary query={quoteQuery}>
        {(quotePayload) => {
          const quote = quotePayload as unknown as QuotePayload;
          return (
            <MetricGrid
              metrics={[
                { label: "Current Price", value: quote.last_traded_price },
                { label: "Bid", value: quote.bid_price },
                { label: "Ask", value: quote.ask_price },
                { label: "Open", value: quote.open },
                { label: "High", value: quote.high },
                { label: "Low", value: quote.low },
                { label: "Close", value: quote.close },
                { label: "Volume", value: quote.volume },
                { label: "Change", value: quote.change },
                { label: "Change %", value: quote.change_percent },
                { label: "Timestamp", value: quote.timestamp },
                { label: "Provider", value: quote.provider },
              ]}
            />
          );
        }}
      </QueryStateBoundary>
      <QueryStateBoundary query={metadataQuery}>
        {(metadataPayload) => {
          const metadata = metadataPayload as unknown as InstrumentMetadataPayload;
          return (
            <MetricGrid
              metrics={[
                { label: "Instrument Type", value: metadata.instrument_type },
                { label: "Exchange", value: instrument.exchange_code },
                { label: "Tick Size", value: metadata.tick_size },
                { label: "Lot Size", value: metadata.lot_size },
              ]}
            />
          );
        }}
      </QueryStateBoundary>
    </StatusCard>
  );
}
